/**
 * @file Factory for number-stepper
 * @author diego.morales@notch-interactive.com
 *
 * @module component/number-stepper
 *
 */

import observer from '../libs/observer';

let defaults = {
    inputAttr: 'data-input',
    stepUpAttr: 'data-step-up',
    stepDownAttr: 'data-step-down',
    validationTimeout: 330,  // milliseconds,
    disableSteppersWhenBoundReached: true
};

const numberStepper = (el, options) => {
    let instance = {},
        input,
        stepUp,
        stepDown,
        settings = Object.assign({}, defaults, options),
        validationTimeoutId = -1,

        validateInput = () => {
            if (validationTimeoutId > -1) {
                clearTimeout(validationTimeoutId);
            }

            validationTimeoutId = setTimeout(() => {

                // When user inputs only '-' or '+' or '.', the value is an empty string. Default to 0.
                let currentValue = input.value === '' ? 0 : Number(input.value);

                // Apply bounds to given value
                if (currentValue <= Number(input.min)) {
                    input.value = input.min;

                    if (settings.disableSteppersWhenBoundReached) {
                        stepDown.disabled = true;
                        stepUp.disabled = false;
                    }

                } else if (currentValue >= Number(input.max)) {
                    input.value = input.max;

                    if (settings.disableSteppersWhenBoundReached) {
                        stepDown.disabled = false;
                        stepUp.disabled = true;
                    }
                } else {
                    input.value = currentValue;

                    stepDown.disabled = false;
                    stepUp.disabled = false;
                }

                validationTimeoutId = -1;

                instance.trigger('validated', {value: Number(input.value)});
            }, settings.validationTimeout);
        },

        stepHandler = (e) => {
            let currentValue = Number(input.value);

            if (e.target.closest(`[${settings.stepUpAttr}]`)) {
                input.value = currentValue += 1;
                validateInput();

                e.preventDefault();
                e.stopPropagation();
                return;
            }

            if (e.target.closest(`[${settings.stepDownAttr}]`)) {
                input.value = currentValue -= 1;
                validateInput();

                e.preventDefault();
                e.stopPropagation();
            }
        },

        bindEvents = () => {

            // IE/EDGE won't fire 'change' event. We have to listen to 'input' event.
            input.addEventListener('input', validateInput);
            el.addEventListener('click', stepHandler);
        },

        unbindEvents = () => {
            input.removeEventListener('input', validateInput);
            el.removeEventListener('click', stepHandler);
        };

    if (!(el instanceof HTMLElement)) {
        return;
    }

    // Cache elements
    input = el.querySelector(`[${settings.inputAttr}]`);
    stepUp = el.querySelector(`[${settings.stepUpAttr}]`);
    stepDown = el.querySelector(`[${settings.stepDownAttr}]`);

    bindEvents();

    // Add composites
    Object.assign(instance, observer());

    // public methods
    instance.setValue = (value) => {
        let number = Number(value);

        if (isNaN(number)) {
            return;
        }

        input.value = number;
        validateInput();
    };

    instance.reset = () => {
        input.value = input.getAttribute('value');
        validateInput();
    };

    instance.getValue = () => Number(input.value);

    instance.destroy = () => {
        unbindEvents();
    };

    return instance;
};


export default numberStepper;

// Export factory to window
window.DS = window.DS || {};
window.DS.numberStepper = numberStepper;
